import React, { useRef, useState } from "react";
import { USER_ROLES } from "../../data/models";
// import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

// Define the Property interface with all image fields
interface Property {
  id: string;
  upi?: string;
  status?: string;
  images?: { url: string }[];

  // Image fields
  frontView?: string;
  backView?: string;
  leftView?: string;
  rightView?: string;
  livingRoom?: string;
  kitchen?: string;
  bathroom?: string;
  bedroom?: string;
  diningRoom?: string;
  veranda?: string;
  googleEarthOne?: string;
  googleEarthTwo?: string;
  masterPlanPhoto?: string;
  photo?: string;
  propertyPhotos?: Array<{
    label: string;
    url: string;
    field?: string;
  }>;

  // Shared location
  country?: "RW" | "UG";
  district?: string;

  // Rwanda-specific
  sector?: string;
  cell?: string;
  village?: string;
  province?: string;

  // Uganda-specific
  region?: string;
  subRegion?: string;
  county?: string;
  subcounty?: string;
  parish?: string;

  mainBuilding?: {
    constructedArea: number;
  };
  landParcel?: {
    area: number;
  };
  valuationDetails?: {
    status?: string;
    totalPropertyValue?: number;
  };
  location?: {
    sector?: string;
    district?: string;
  };
}

// Define the component props interface
interface PropertyCardProps {
  property: Property;
  onClick: () => void;
  userRole: string | null;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onClick,
  userRole,
}) => {
  const galleryRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  // Get all available images from the property
  // Helper function to get all property images
const getAllPropertyImages = (
  property: Property
): Array<{ url: string; label: string }> => {
  const images: Array<{ url: string; label: string }> = [];

  // Use propertyPhotos as the primary source if available
  if (property.propertyPhotos && Array.isArray(property.propertyPhotos)) {
    return property.propertyPhotos
      .filter((photo: any) => photo.url)
      .map((photo: any, index) => ({
        url: photo.url,
        label: photo.label || `Property Image ${index + 1}`,
      }));
  }

  // Fallback to individual image fields only if propertyPhotos is empty
  const imageFields = [
    { field: property.frontView, label: "Front View" },
    { field: property.backView, label: "Back View" },
    { field: property.leftView, label: "Left View" },
    { field: property.rightView, label: "Right View" },
    { field: property.livingRoom, label: "Living Room" },
    { field: property.kitchen, label: "Kitchen" },
    { field: property.bathroom, label: "Bathroom" },
    { field: property.bedroom, label: "Bedroom" },
    { field: property.diningRoom, label: "Dining Room" },
    { field: property.veranda, label: "Veranda" },
    { field: property.googleEarthOne, label: "Google Earth One" },
    { field: property.googleEarthTwo, label: "Google Earth Two" },
    { field: property.masterPlanPhoto, label: "Master Plan" },
    { field: property.photo, label: "Property Photo" },
  ];

  imageFields.forEach(({ field, label }) => {
    if (field) {
      images.push({ url: field, label });
    }
  });

  return images;
};

  const images = getAllPropertyImages(property);
  const hasImages = images.length > 0;
  
  const handleScroll = () => {
    if (galleryRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = galleryRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const getStatusDetails = () => {
    const status = property.valuationDetails?.status || property.status || "Unknown";
    switch (status.toLowerCase()) {
      case "completed":
        return { label: "Completed", color: "bg-green-100 text-green-800" };
      case "in progress":
        return { label: "In Progress", color: "bg-yellow-100 text-yellow-800" };
      case "pending":
        return { label: "Pending Approval", color: "bg-blue-100 text-blue-800" };
      default:
        return { label: "New", color: "bg-gray-100 text-gray-800" };
    }
  };

  const statusDetails = getStatusDetails();
  const isComplete = property.mainBuilding && property.landParcel;
  
  // Get default image for fallback
  const defaultImage = "https://res.cloudinary.com/karera/image/upload/fl_preserve_transparency/v1764450776/bow6kbfcmnrv9z5kprgp.jpg?_s=public-apps";
// console.log("PropertyCard rendered with property:", property);
  return (
    <div
      className="bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer transition-all hover:shadow-md"
      onClick={onClick}
    >
      {/* Property Image Gallery */}
      <div className="h-48 relative overflow-hidden">
        {hasImages ? (
          <>
            {/* Image Gallery with Scroll */}
            <div 
              ref={galleryRef}
              className="flex h-full overflow-x-auto scrollbar-hide scroll-smooth"
              onScroll={handleScroll}
            >
              {images.map((image, index) => (
                <div 
                  key={index} 
                  className="flex-shrink-0 w-full h-full relative group"
                >
                  <img
                    src={image.url}
                    alt={image.label}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultImage;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="absolute bottom-2 left-2 text-white text-xs font-medium px-2 py-1 bg-black/60 rounded">
                      {image.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                {showLeftArrow && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollLeft();
                    }}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full transition-all duration-200"
                  >
                    {/* <ChevronLeftIcon className="h-5 w-5" /> */}
                  </button>
                )}
                {showRightArrow && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollRight();
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full transition-all duration-200"
                  >
                    {/* <ChevronRightIcon className="h-5 w-5" /> */}
                  </button>
                )}
              </>
            )}
            
            {/* Image Counter */}
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              {images.length} {images.length === 1 ? 'image' : 'images'}
            </div>
          </>
        ) : (
          // No images fallback
          <div className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 mb-1">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">No images available</p>
            </div>
          </div>
        )}
        
        {/* Status Badge */}
        <div
          className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${statusDetails.color}`}
        >
          {statusDetails.label}
        </div>
      </div>

      {/* Property Info */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 mb-1">
          {property?.country === "UG" ? (
            <div className="flex flex-col gap-0.5">
              {property?.region && (
                <span className="text-xs text-gray-500">
                  {property.region}
                  {property?.subRegion ? ` · ${property.subRegion}` : ""}
                </span>
              )}
              <span>
                {property?.district || "Unknown District"}
                {property?.county ? `, ${property.county}` : ""}
              </span>
            </div>
          ) : (
            `${property?.sector || property?.location?.sector || "Unknown"}, ${
              property?.district || property?.location?.district || "Unknown"
            }`
          )}
        </h3>

        <p className="text-sm text-gray-500 mb-3">
          UPI: {property.upi || "Pending"} 
        </p>

        {/* Property Details - show if complete */}
        {isComplete && (
          <div className="space-y-2 mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Land Area:</span>
              <span className="font-medium">
                {property.landParcel?.area} m²
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Building Area:</span>
              <span className="font-medium">
                {property.mainBuilding?.constructedArea} m²
              </span>
            </div>
            {property.valuationDetails?.totalPropertyValue && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Property Value:</span>
                <span className="font-medium">
                  $
                  {property.valuationDetails.totalPropertyValue.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Role-specific action prompt */}
        <div className="flex items-center mt-3 pt-3 border-t border-gray-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-indigo-500 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {userRole === USER_ROLES.CLIENT ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            ) : userRole === USER_ROLES.DATA_COLLECTOR ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            )}
          </svg>
          <span className="text-xs text-indigo-600 font-medium">
            {userRole === USER_ROLES.CLIENT
  ? "View property details"
  : userRole === USER_ROLES.DATA_COLLECTOR
  ? "Continue data collection"
  : userRole === USER_ROLES.ADMIN
  ? "Manage system settings"
  : userRole === USER_ROLES.INSTITUTIONAL_CLIENT
  ? "View portfolio analytics"
  : userRole === USER_ROLES.SUPERVISOR
  ? "Review & approve"
  : userRole === USER_ROLES.SYSTEM
  ? "Execute automated tasks"
  : "View property details"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;